"use client";

/**
 * Enrollments tab surface (Phase J · Tasks 55-58).
 *
 * Mounted from the sequence-detail tabs strip
 * (`features/sequences/components/detail/tabs.tsx`) under the
 * `enrollments` tab value. Composes the filter strip, the search
 * input, the table, and (Task 57) the row actions menu.
 *
 * State ownership:
 *   - Server state: `useEnrollmentsList` (Orval-backed) for the page
 *     query + the four mutations (pause / resume / cancel / restart).
 *   - Local React state: status chip + search input + page index. We
 *     deliberately do not push these to the URL because the host page
 *     already owns `?tab=enrollments` and we don't want to clobber
 *     other tabs' filter state.
 *
 * Search-by-name is client-side because the API doesn't yet expose a
 * search param (spec §10.1 lists `status`, `page`, `per_page` only).
 * It filters the visible page after `useGetProfile` resolves each
 * row's name. When the backend lands a `?q=` param we'll forward it.
 *
 * Spec refs:
 * - §13.1 (`Enrollments.Root`, `Enrollments.Filters`, `Enrollments.Table`).
 * - §10.1 list endpoint, §10.4 action semantics.
 *
 * Figma: the list view is intentionally not in the spec §2 reference
 * map. Per AGENTS.md "Building New Features Not in Figma — CRITICAL",
 * visual chrome is derived from `tasks/components/list/table.tsx` and
 * the existing `audit-log.tsx` row treatment.
 */

import { useMemo, useState } from "react";

import { Icon } from "@/components/ui/icon";
import { TablePagination } from "@/components/ui/table";
import { useGetSequence } from "@/lib/api/generated";

import {
  useEnrollmentsList,
  ENROLLMENTS_PER_PAGE,
} from "../../hooks/use-enrollments-list";
import type { EnrollmentListItem, EnrollmentStatus } from "../../lib/types";

import { ActionsMenu } from "./actions-menu";
import { EnrollmentDetailDrawer } from "./detail-drawer";
import { EnrollmentTable } from "./table";
import { StatusFilterStrip } from "./status-filter";

export interface EnrollmentListProps {
  /** Owning job — required to satisfy the route's `/jobs/{jobId}`
   *  segment per spec §10.1. */
  jobId: string;
  /** Sequence whose enrollments are being shown. Locked into every
   *  query so the user can't widen scope by clearing a filter. */
  sequenceId: string;
}

export function EnrollmentList({ jobId, sequenceId }: EnrollmentListProps) {
  const [statusFilter, setStatusFilter] = useState<EnrollmentStatus | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [drawerEnrollment, setDrawerEnrollment] =
    useState<EnrollmentListItem | null>(null);

  const sequence = useGetSequence(jobId, sequenceId, {
    query: { enabled: Boolean(jobId && sequenceId) },
  });

  const { rows, total, isLoading, isFetching, isError } = useEnrollmentsList({
    jobId,
    sequenceId,
    status: statusFilter,
    page,
  });

  const trimmedSearch = search.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;

  const visibleRows = useMemo<EnrollmentListItem[]>(() => {
    if (!isSearching) return rows;
    return rows.filter((row) =>
      row.profile_id.toLowerCase().includes(trimmedSearch),
    );
  }, [rows, isSearching, trimmedSearch]);

  const handleStatusChange = (next: EnrollmentStatus | null) => {
    setStatusFilter(next);
    setPage(1);
  };

  const handleSearch = (next: string) => {
    setSearch(next);
    if (page !== 1) setPage(1);
  };

  return (
    <div
      data-testid="enrollment-list"
      className="flex h-full min-h-0 flex-1 flex-col"
    >
      <div className="flex flex-col gap-2 border-b border-border bg-surface-elevated px-page py-3">
        <div className="flex flex-wrap items-center justify-between gap-2 md:flex-nowrap md:gap-3">
          <StatusFilterStrip
            active={statusFilter}
            onChange={handleStatusChange}
          />
          <SearchInput value={search} onChange={handleSearch} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {isError ? (
          <ListErrorState />
        ) : (
          <EnrollmentTable
            rows={visibleRows}
            isLoading={isLoading || sequence.isLoading}
            sequence={sequence.data}
            emptyVariant={
              statusFilter !== null || isSearching
                ? "no-results"
                : "no-enrollments"
            }
            onRowClick={(enrollment) => setDrawerEnrollment(enrollment)}
            renderActions={(enrollment, actions) => (
              <ActionsMenu
                enrollment={enrollment}
                jobId={jobId}
                sequenceId={sequenceId}
                actions={actions}
              />
            )}
            jobId={jobId}
            sequenceId={sequenceId}
          />
        )}
        {!isError && total > 0 && (
          <TablePagination
            page={page}
            total={total}
            perPage={ENROLLMENTS_PER_PAGE}
            onPageChange={setPage}
            label={(t) => `${t} ${t === 1 ? "enrollment" : "enrollments"}`}
            disabled={isFetching}
          />
        )}
      </div>

      <EnrollmentDetailDrawer
        enrollment={drawerEnrollment}
        jobId={jobId}
        sequenceId={sequenceId}
        open={drawerEnrollment !== null}
        onOpenChange={(next) => {
          if (!next) setDrawerEnrollment(null);
        }}
      />
    </div>
  );
}

interface SearchInputProps {
  value: string;
  onChange: (next: string) => void;
}

function SearchInput({ value, onChange }: SearchInputProps) {
  return (
    <label className="relative flex w-full items-center md:w-56">
      <span className="absolute left-2.5 text-muted-foreground">
        <Icon name="IconMagnifyingGlass" size={14} />
      </span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search candidates"
        aria-label="Search enrollments by candidate"
        data-testid="enrollment-search"
        className="h-8 w-full rounded-md border border-border bg-white/4 pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
      />
    </label>
  );
}

function ListErrorState() {
  return (
    <div
      role="alert"
      data-testid="enrollment-list-error"
      className="flex flex-1 flex-col items-center justify-center gap-2 px-page py-16 text-center"
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
        <Icon
          name="IconExclamationCircle"
          size={20}
          className="text-destructive"
        />
      </div>
      <h3 className="text-base font-medium text-heading">
        Couldn&apos;t load enrollments
      </h3>
      <p className="max-w-sm text-sm text-muted-foreground">
        Try switching tabs and back, or reload the page. If this keeps
        happening, contact support.
      </p>
    </div>
  );
}
